<template>
  <plan-card :class="['grid', isEos && payoutCount > 0 ? 'grid-cols-5': 'grid-cols-4']">
    <div
      v-if="isEos && payoutCount > 0"
      class="grid__item"
    >
      <section>
        <h3>
          <QuestionBadge :id="'pendo-eos-period-payment'">
            {{ periodLetter }}{{ payoutCount }} Payment
          </QuestionBadge>
        </h3>
        <p class="sub-header">
          Date: {{ formatDate(periodDate) }}
        </p>
        <p class="currency-eos">
          {{ formatCurrency(periodPayment) }}
        </p>
        <h3>Paid To Date</h3>
        <p class="currency-eos">
          {{ formatCurrency(latestPaidToDate) }}
        </p>
      </section>
    </div>
    <div class="grid__item">
      <section>
        <h3 id="overview-forecast-label">
          {{ selected.forecast.label }}
        </h3>
        <p
          v-if="isPercentOfSalary"
          class="sub-header"
        >
          <template v-if="isEos && selected.distributions.length">
            {{ formatPercent(selected.distributions[selected.distributions.length - 1].percent_of_salary, 1) }}
          </template>
          <template v-else>
            {{ formatPercent(selected.employee.salary.plan ? (selected.forecast.adjusted / selected.employee.salary.plan) * 10000000 : 0, 1) }}
          </template> of Plan <span>{{ salaryTerm }}</span>
        </p>
        <p class="currency">
          <template v-if="isEos && selected.distributions.length">
            {{ formatCurrency(selected.distributions[selected.distributions.length - 1].forecasted_annual_award) }}
          </template>
          <template v-else>
            {{ formatCurrency(selected.forecast.adjusted) }}
          </template>
        </p>
        <p
          v-if="!isFinal"
          class="info"
        >
          Your current projected plan award value.
        </p>
      </section>
    </div>
    <div class="grid__item">
      <section>
        <h3 id="overview-base-label">
          {{ selected.tiers.base.label }}
        </h3>
        <p
          v-if="isPercentOfSalary"
          class="sub-header"
        >
          {{ formatPercent(selected.tiers.base.percent, 1) }} of Plan <span>{{ salaryTerm }}</span>
        </p>
        <p class="currency">
          {{ formatCurrency(selected.tiers.base.value) }}
        </p>
        <p class="info">
          Your plan opportunity if minimum company results are achieved.
        </p>
      </section>
    </div>
    <div class="grid__item">
      <section>
        <h3 id="overview-target-label">
          {{ selected.tiers.target.label }}
        </h3>
        <p
          v-if="isPercentOfSalary"
          class="sub-header"
        >
          {{ formatPercent(selected.tiers.target.percent, 1) }} of Plan <span>{{ salaryTerm }}</span>
        </p>
        <p class="currency">
          {{ formatCurrency(selected.tiers.target.value) }}
        </p>
        <p class="info">
          Your plan opportunity assuming budgeted financial results are met.
        </p>
      </section>
    </div>
    <div class="grid__item">
      <section>
        <h3 id="overview-superior-label">
          {{ selected.tiers.superior.label }}
        </h3>
        <p
          v-if="isPercentOfSalary"
          class="sub-header"
        >
          {{ formatPercent(selected.tiers.superior.percent, 1) }} of Plan <span>{{ salaryTerm }}</span>
        </p>
        <p class="currency">
          {{ formatCurrency(selected.tiers.superior.value) }}
        </p>
        <p class="info">
          Your plan opportunity if financial results are exceptional.
        </p>
      </section>
    </div>
  </plan-card>
</template>

<script>
import PlanCard from "@/components/plans/global/PlanCard.vue";
import QuestionBadge from "@/components/pendo/QuestionBadge.vue";
import VLcurrency from "@/composables/VLcurrency";
import VLdate from "@/composables/VLdate";
import VLpercent from '@/composables/VLpercent';

export default {
  name: "PlanOverview",

  components: {
    QuestionBadge,
    PlanCard,
  },

  props: {
    selected: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {}
  },

  computed: {
    planMode() {
      return this.selected.plan.opportunity_mode
    },
    isFinal() {
      return this.selected.plan.isPaymentsFinalized
    },
    isPercentOfSalary() {
      return this.selected.plan.opportunity_mode === 'as-pct'
    },
    periodLetter() {
      if(this.payoutTotal === 4) return 'Q'
      else if(this.payoutTotal === 3) return 'T'
      else return 'H'
    },
    isEos() {
      return !!this.selected.plan?.isEos;
    },
    payoutCount() {
      if(this.selected?.distributions?.length) return this.selected.distributions.length
      else return 0
    },
    payoutTotal() {
      if(this.selected.plan?.holdback?.payout_count) return this.selected.plan.holdback.payout_count
      else return 0
    },
    periodPayment() {
      if(this.selected?.distributions?.length) return this.selected.distributions[this.selected.distributions.length - 1].award_amount
      else return null
    },
    latestPaidToDate() {
      if(this.selected?.distributions?.length) return this.selected.distributions[this.selected.distributions.length - 1].ytd_bonus
      else return null
    },
    periodDate() {
      if(this.selected?.distributions?.length) return this.selected.distributions[this.selected.distributions.length - 1].date
      else return null
    },
    salaryTerm() {
      return this.selected.employee.salary.label
    },
  },

  methods: {
    formatCurrency(value, decimals = 0, currency = 'USD') {
        return VLcurrency.formatCurrency(value, decimals, currency);
    },
    formatDate(d, format = 'YYYY-MM-DD', preset = 'DEFAULT') {
      return VLdate.formatDate(d, format, preset);
    },
    formatPercent(value, decimals = 0) {
        return VLpercent.formatPercent(value, decimals);
    }
  }
}
</script>

<style lang="scss" scoped>
@use "../../../scss/_variables.scss";

.plan-card {
  min-height: 140px;
}

section {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;

  text-align: center;
  padding-top: 1rem;
  padding-bottom: 1rem;

  h3, p {
    text-align: center;
  }
}

h3 {
  font-size: variables.$text-base;
  font-weight: bold;

  text-transform: capitalize;
}

.sub-header {
  font-size: variables.$text-sm;

  span {
    text-transform: capitalize;
  }
}

.currency {
  font-family: variables.$font-secondary;
  font-size: variables.$text-xl;
  font-weight: 800;
  color: var(--highlight-color);

  &-eos {
    font-size: variables.$text-lg;
    font-family: variables.$font-secondary;
    font-weight: 800;
    color: var(--highlight-color);
  }

  &-eos:first-letter,
  &:first-letter {
    vertical-align: super;
    font-size: variables.$text-sm;
    letter-spacing: 4px;
  }
}

.info {
  font-size: variables.$text-xs;
}

.grid {
  --gap: 2rem;
  --line-offset: calc(var(--gap) / 2);
  --line-thickness: 1px;

  display: grid;
  grid-template-columns: repeat(4, 1fr);
  overflow: hidden;
  gap: var(--gap);
}
.grid__item {
  position: relative;
}
.grid__item::before {
  content: '';
  position: absolute;
  background-color: variables.$charcoal-tint-20;
  z-index: 1;
}
.grid__item::before {
  inline-size: var(--line-thickness);
  block-size: 100vh;
  inset-inline-start: calc(var(--line-offset) * -1);
}

.grid-cols-4 {
  grid-template-columns: repeat(4, 1fr);
}

.grid-cols-5 {
  grid-template-columns: repeat(5, 1fr);
}

@media screen and (max-width: 1200px) {
  .grid-cols-5 {
    grid-template-columns: repeat(1, 1fr);
  }
}
@media screen and (max-width: 900px) {
  .grid-cols-4 {
    grid-template-columns: repeat(1, 1fr);
  }
}
</style>